import { useUIStore } from '@/store/uiStore';
import { projectService } from '@/services/projectService';
import ProjectAddressSyncModal from '@/components/projects/ProjectAddressSyncModal.vue';

const ADDRESS_KEYS = [
  ['street'],
  ['house_number', 'houseNumber', 'number'],
  ['postal_code', 'postalCode', 'zip'],
  ['city'],
];

/**
 * Comparable form of an address, tolerant of key variants and formatting.
 * Mirrors the backend signature in utils/projectAddressSync.js.
 */
export function addressSignature(address) {
  if (!address || typeof address !== 'object') return '';
  return ADDRESS_KEYS.map((keys) => {
    const key = keys.find((candidate) => String(address[candidate] || '').trim());
    return String(key ? address[key] : '')
      .toLowerCase()
      .replace(/\s+/g, '')
      .trim();
  }).join('|');
}

/**
 * A project snapshots the customer address when it is created, so correcting
 * the address on the lead leaves open projects (and their work orders) behind.
 * After saving a lead address, call this to offer updating those projects.
 */
export function useLeadAddressSync() {
  const uiStore = useUIStore();

  /**
   * @param {string} leadId
   * @param {Object} options
   * @param {Object} [options.previousAddress] - address the lead had before the change,
   *   so projects with a deliberately different address are left out
   * @param {Function} [options.onApplied] - called after projects were updated
   * @param {Function} [$t] - i18n function
   * @returns {Promise<number>} number of outdated projects found
   */
  async function promptAddressSync(leadId, { previousAddress = null, onApplied = null } = {}, $t) {
    if (!leadId) return 0;

    const t = typeof $t === 'function' ? $t : (key) => key;

    let result;
    try {
      result = await projectService.previewLeadAddressSync(leadId, previousAddress);
    } catch (error) {
      // A failed check must never block the lead save itself
      console.error('Error checking outdated project addresses:', error);
      return 0;
    }

    const projects = result?.projects || [];
    if (!projects.length) return 0;

    uiStore.addModal(ProjectAddressSyncModal, {
      title: t('projects.address_sync_title'),
      size: 'md',
      leadId,
      leadAddress: result.lead_address,
      projects,
      onApplied,
    });

    return projects.length;
  }

  return { promptAddressSync };
}
